import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Server } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || '';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function PctBar({ value, warn = 70, crit = 90 }) {
  if (value == null) return <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>—</span>;
  const v = parseFloat(value);
  const color = v >= crit ? 'var(--red)' : v >= warn ? 'var(--amber)' : 'var(--green)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 5, background: 'var(--bg)', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.min(v, 100)}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
      <span style={{ fontSize: 12, fontFamily: 'monospace', color, fontWeight: 600, minWidth: 38 }}>
        {v}%
      </span>
    </div>
  );
}

function StatusDot({ lastSeen }) {
  if (!lastSeen) return <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>—</span>;
  const ageMin = (Date.now() - new Date(lastSeen)) / 60000;
  const active = ageMin < 30;
  return (
    <span title={active ? 'Activo (< 30 min)' : 'Sin reportes recientes'}
      style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%',
        background: active ? 'var(--green)' : 'var(--amber)', marginRight: 6 }} />
  );
}

const TABS = [
  { id: 'servers', label: 'Servidores / Equipos' },
  { id: 'network', label: 'Red' },
  { id: 'storage', label: 'Almacenamiento' },
];

export default function MonitoringOnPremise() {
  const [assets, setAssets]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [tab, setTab]           = useState('servers');
  const [search, setSearch]     = useState('');
  const [stateFilter, setStateFilter] = useState('all'); // 'all' | 'online' | 'offline'
  const [osFilter, setOsFilter] = useState('all');

  const token = localStorage.getItem('token');

  const load = useCallback(() => {
    setLoading(true); setError(null);
    fetch(`${API_URL}/api/monitoring/onpremise/overview`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { if (d.error) throw new Error(d.error); setAssets(Array.isArray(d) ? d : []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const tabStyle = (active) => ({
    padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
    fontSize: 13, fontWeight: active ? 700 : 400,
    color: active ? 'var(--accent)' : 'var(--ink-2)',
    borderBottom: `2px solid ${active ? 'var(--accent)' : 'transparent'}`,
    marginBottom: -2,
  });

  // Compute summary counts
  const isOnline = (a) => a.last_seen && (Date.now() - new Date(a.last_seen)) / 60000 < 30;
  const online  = assets.filter(isOnline).length;
  const offline = assets.length - online;

  // Unique OS list for filter
  const osList = ['all', ...new Set(assets.map(a => a.os_name).filter(Boolean))];

  // Filtered assets
  const filteredAssets = assets.filter(a => {
    const matchSearch = !search ||
      (a.hostname || '').toLowerCase().includes(search.toLowerCase()) ||
      (a.ip_address || '').toLowerCase().includes(search.toLowerCase());
    const matchState = stateFilter === 'all' ||
      (stateFilter === 'online' && isOnline(a)) ||
      (stateFilter === 'offline' && !isOnline(a));
    const matchOs = osFilter === 'all' || a.os_name === osFilter;
    return matchSearch && matchState && matchOs;
  });

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Monitoreo On-Premise</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--ink-2)', fontSize: 14 }}>
            Estado de los equipos gestionados por el agente ITIL
          </p>
        </div>
        <button className="btn btn-secondary" onClick={load}
          style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <RefreshCw size={14} />
          Actualizar
        </button>
      </div>

      {/* Summary strip */}
      {!loading && !error && assets.length > 0 && (
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          <div style={{ padding: '10px 20px', border: '1px solid var(--border)', borderRadius: 8,
            background: 'var(--surface)', fontSize: 13 }}>
            <span style={{ fontWeight: 700, fontSize: 20, color: 'var(--ink)' }}>{assets.length}</span>
            <span style={{ color: 'var(--ink-2)', marginLeft: 6 }}>equipos con agente</span>
          </div>
          <div style={{ padding: '10px 20px', border: '1px solid var(--green-dim)', borderRadius: 8,
            background: 'var(--green-dim)', fontSize: 13 }}>
            <span style={{ fontWeight: 700, fontSize: 20, color: 'var(--green)' }}>{online}</span>
            <span style={{ color: 'var(--ink-2)', marginLeft: 6 }}>activos</span>
          </div>
          {offline > 0 && (
            <div style={{ padding: '10px 20px', border: '1px solid var(--amber-dim)', borderRadius: 8,
              background: 'var(--amber-dim)', fontSize: 13 }}>
              <span style={{ fontWeight: 700, fontSize: 20, color: 'var(--amber)' }}>{offline}</span>
              <span style={{ color: 'var(--ink-2)', marginLeft: 6 }}>sin reportes</span>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border)', marginBottom: 24, gap: 2 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={tabStyle(tab === t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Servers tab */}
      {tab === 'servers' && (
        <>
          {loading && (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--ink-3)', fontSize: 13 }}>
              Cargando equipos…
            </div>
          )}
          {error && (
            <div style={{ padding: 24, color: 'var(--red)', fontSize: 13 }}>Error: {error}</div>
          )}
          {!loading && !error && assets.length === 0 && (
            <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--ink-3)' }}>
              <Server size={40} style={{ opacity: 0.3, marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
              <div style={{ fontSize: 14 }}>Sin equipos con agente activo</div>
            </div>
          )}
          {!loading && !error && assets.length > 0 && (
            <>
              {/* Filter bar */}
              <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap', alignItems:'center' }}>
                <input
                  value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="Buscar por hostname o IP…"
                  style={{ flex:1, minWidth:180, padding:'6px 10px', border:'1px solid var(--border-2)',
                    borderRadius:7, fontSize:13, color:'var(--ink)' }}
                />
                <div style={{ display:'flex', gap:4 }}>
                  {[['all','Todos'], ['online','Activos'], ['offline','Sin reporte']].map(([val, lbl]) => (
                    <button key={val} onClick={() => setStateFilter(val)}
                      style={{ padding:'5px 12px', fontSize:12, borderRadius:6, border:'1px solid var(--border-2)',
                        background: stateFilter === val ? 'var(--accent)' : '#fff',
                        color: stateFilter === val ? '#fff' : 'var(--ink-2)',
                        fontWeight: stateFilter === val ? 700 : 400, cursor:'pointer' }}>
                      {lbl}
                    </button>
                  ))}
                </div>
                {osList.length > 2 && (
                  <select value={osFilter} onChange={e => setOsFilter(e.target.value)}
                    style={{ padding:'5px 10px', fontSize:12, borderRadius:6, border:'1px solid var(--border-2)',
                      background:'var(--surface)', color:'var(--ink-2)', cursor:'pointer' }}>
                    {osList.map(os => (
                      <option key={os} value={os}>{os === 'all' ? 'Todos los OS' : os}</option>
                    ))}
                  </select>
                )}
                <span style={{ fontSize:12, color:'var(--ink-2)' }}>
                  {filteredAssets.length} de {assets.length} equipo(s)
                </span>
              </div>
            <div className="card">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Equipo</th>
                      <th style={{ width: 150 }}>OS</th>
                      <th style={{ width: 200 }}>CPU</th>
                      <th style={{ width: 200 }}>RAM</th>
                      <th style={{ width: 200 }}>Disco</th>
                      <th style={{ width: 140 }}>Último reporte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssets.map(a => (
                      <tr key={a.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center' }}>
                            <StatusDot lastSeen={a.last_seen} />
                            <span style={{ fontWeight: 500, fontSize: 13 }}>{a.hostname}</span>
                          </div>
                          <div style={{ fontSize: 11, color: 'var(--ink-3)', paddingLeft: 14 }}>
                            {a.ip_address || a.user_name}
                          </div>
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{a.os_name || '—'}</td>
                        <td>
                          {a.cpu_cores && (
                            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>
                              {a.cpu_cores} cores
                            </div>
                          )}
                          <PctBar value={null} />
                        </td>
                        <td>
                          {a.ram_total_gb && (
                            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>
                              {a.ram_used_gb != null ? `${a.ram_used_gb} / ` : ''}{a.ram_total_gb} GB
                            </div>
                          )}
                          <PctBar value={a.ram_pct} />
                        </td>
                        <td>
                          {a.disk_total_gb && (
                            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginBottom: 4 }}>
                              {a.disk_used_gb != null ? `${a.disk_used_gb} / ` : ''}{a.disk_total_gb} GB
                            </div>
                          )}
                          <PctBar value={a.disk_pct} />
                        </td>
                        <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{fmtDate(a.last_seen)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            </>
          )}
        </>
      )}

      {/* Other tabs */}
      {tab !== 'servers' && (
        <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--ink-3)' }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Próximamente</div>
          <div style={{ fontSize: 14 }}>
            {tab === 'network'
              ? 'Monitoreo de red vía SNMP y NetFlow en desarrollo.'
              : 'Monitoreo de almacenamiento y NAS en desarrollo.'}
          </div>
        </div>
      )}
    </div>
  );
}
